import { HashMap } from '@ngneat/transloco';
import {
  MAX_MAIL_REQUESTS_EXCEEDED_NOTIFICATION,
  NEW_EMAIL_FAILED_NOTIFICATION,
  NEW_EMAIL_FAILED_SERVER_UNREACHABLE_NOTIFICATION,
  NEW_EMAIL_SENT_NOTIFICATION,
  VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION,
  YATTA_SUPPORT_EMAIL_ADDRESS
} from '@platform-frontend/platform';
import { DateParams, buildNotificationTranslationId } from '@platform-frontend/transloco';
import {
  TranslationId,
  YattaExternalLanguageSpecificLinkUrl,
  buildMailToLink,
  buildNotificationActionLink,
  buildYattaExternalLink
} from '@platform-frontend/util';
import { ACCOUNT_API_TRANSLOCO_SCOPE } from './account-api-transloco-scope';
import { GotoReactivateAccount } from './disabled-details/disabled-details.actions';
import { ResendAuthenticationEmail } from './email-authentication/email-authentication.actions';
import { ResendPasswordUpdateMail } from './password-update/password-update.actions';
import { GotoSignIn } from './session/session.actions';

const buildTranslationId: (key: string, params?: HashMap) => TranslationId = (key: string, params: HashMap = {}) =>
  buildNotificationTranslationId(key, params, ACCOUNT_API_TRANSLOCO_SCOPE);

// Shared messages
const USER_NOT_KNOWN_NOTIFICATION = buildTranslationId('shared.userNotKnown', { signUpLink: buildNotificationActionLink(new GotoSignIn()) });
const OAUTH_AUTHENTICATION_FAILED = (provider: string): TranslationId => buildTranslationId('shared.oauthAuthenticationFailed', { provider });
const SIGNED_IN_WITH_ANOTHER_ACCOUNT = buildTranslationId('shared.signedInWithAnotherAccount');
const DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS = (emailAddress: string): TranslationId =>
  buildTranslationId('shared.duplicateEmailAddressDifferentAccounts.message', {
    supportMailLink: buildMailToLink(
      YATTA_SUPPORT_EMAIL_ADDRESS,
      buildTranslationId('shared.duplicateEmailAddressDifferentAccounts.mailToYattaSupport.subject', {
        emailAddress
      }),
      null,
      '9a9884ff-e128-4f6a-8673-6d9f474008ee'
    )
  });
const LINK_ACCOUNT_CONFLICT = (vendor: string): TranslationId => buildTranslationId('shared.linkAccountConflict', { vendor });
const LINK_ACCOUNT_MISMATCH = (vendor: string): TranslationId => buildTranslationId('shared.linkAccountMismatch', { vendor });
export const INVALID_PASSWORD_NOTIFICATION = buildTranslationId('shared.invalidPassword');
export const FETCHING_ACCOUNT_META_DATA_FAILED_NOTIFICATION = buildTranslationId('shared.fetchingAccountMetaDataFailed');

// Global account-related errors
export const MSG_ACCOUNT_INVALID_TOKEN = buildTranslationId('account.invalidToken');
export const MSG_ACCOUNT_INACTIVE_TOKEN = buildTranslationId('account.inactiveToken');
export const MSG_ACCOUNT_LINK_ACCOUNT_CONFLICT = LINK_ACCOUNT_CONFLICT;
export const MSG_ACCOUNT_LINK_ACCOUNT_MISMATCH = LINK_ACCOUNT_MISMATCH;

// Sign in
const SIGN_IN_AFTER_ACTION_AUTOMATICALLY_FAILED = (action: 'signUp' | 'updatePassword'): TranslationId =>
  buildTranslationId('signIn.afterActionAutomaticallyFailed', { action });
export const MSG_SIGN_IN_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS = DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS;
export const MSG_SIGN_IN_AFTER_VERIFY_EMAIL_AUTOMATICALLY_FAILED = SIGN_IN_AFTER_ACTION_AUTOMATICALLY_FAILED('signUp');
export const MSG_SIGN_IN_AFTER_UPDATE_PASSWORD_AUTOMATICALLY_FAILED = SIGN_IN_AFTER_ACTION_AUTOMATICALLY_FAILED('updatePassword');
export const MSG_SIGN_IN_USER_NOT_KNOWN = USER_NOT_KNOWN_NOTIFICATION;
export const MSG_SIGN_IN_INVALID_PASSWORD = INVALID_PASSWORD_NOTIFICATION;
export const MSG_SIGN_IN_DEFAULT = buildTranslationId('signIn.default');

// Account disabled
export const MSG_DISABLED_DETAILS_DEFAULT = FETCHING_ACCOUNT_META_DATA_FAILED_NOTIFICATION;
export const MSG_DISABLED_ACCOUNT = (disabledDate: Date, days: number): TranslationId => {
  const disabledDateParams: DateParams = { date: disabledDate, options: { dateStyle: 'long' } };
  return buildTranslationId('disabledAccount.startupMessage', {
    disabledDate: disabledDateParams,
    days,
    reactivateAccountLink: buildNotificationActionLink(new GotoReactivateAccount(), undefined, '28ce3b87-f1c3-487d-8c65-25d847855f48')
  });
};
export const MSG_REACTIVATED_ACCOUNT = buildTranslationId('reactivatedAccount');
export const MSG_DISABLED_ACTION_WITH_DISABLED_ACCOUNT = buildTranslationId('disabledAccount.disabledAction', {
  reactivateAccountLink: buildNotificationActionLink(new GotoReactivateAccount(), undefined, 'cd7d6d78-362d-44dd-ae5c-fec9ecef482f')
});

// Request new password
export const MSG_REQUEST_PASSWORD_RESET_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS = DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS;
export const MSG_REQUEST_PASSWORD_RESET_MAX_MAIL_REQUESTS_EXCEEDED = MAX_MAIL_REQUESTS_EXCEEDED_NOTIFICATION;
export const MSG_REQUEST_PASSWORD_RESET_USER_NOT_KNOWN = USER_NOT_KNOWN_NOTIFICATION;
export const MSG_REQUEST_PASSWORD_RESET_DEFAULT = NEW_EMAIL_FAILED_NOTIFICATION();

// Verify password update
export const MSG_VERIFY_PASSWORD_UPDATE_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;
export const MSG_VERIFY_PASSWORD_UPDATE_SIGNED_IN_WITH_ANOTHER_ACCOUNT = SIGNED_IN_WITH_ANOTHER_ACCOUNT;

// Resend password update mail
export const MSG_RESEND_PASSWORD_UPDATE_MAIL_MAX_MAIL_REQUESTS_EXCEEDED = MAX_MAIL_REQUESTS_EXCEEDED_NOTIFICATION;
export const MSG_RESEND_PASSWORD_UPDATE_MAIL_SERVER_UNREACHABLE = NEW_EMAIL_FAILED_SERVER_UNREACHABLE_NOTIFICATION(new ResendPasswordUpdateMail());
export const MSG_RESEND_PASSWORD_UPDATE_MAIL_DEFAULT = NEW_EMAIL_FAILED_NOTIFICATION(new ResendPasswordUpdateMail());
export const MSG_RESEND_PASSWORD_UPDATE_MAIL_SUCCESS = NEW_EMAIL_SENT_NOTIFICATION;

// Set or change password
export const UPDATE_PASSWORD_FAILED_ALREADY_USED_NOTIFICATION = buildTranslationId('setOrChangePassword.alreadyUsed');
const UPDATE_PASSWORD_RESTORE_ID_EXPIRED_NOTIFICATION = (context: 'changePassword' | 'setPassword'): TranslationId =>
  buildTranslationId('setOrChangePassword.restoreIdExpired', { context });
const UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL_NOTIFICATION = (context: 'changePassword' | 'setPassword'): TranslationId =>
  buildTranslationId('setOrChangePassword.restoreIdNotMatchingEmail', { context });
const UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF_NOTIFICATION = (context: 'changePassword' | 'setPassword'): TranslationId =>
  buildTranslationId('setOrChangePassword.restoreIdNotMatchingVerificationRef', { context });
export const UPDATE_PASSWORD_SUCCESS_NOTIFICATION = (context: 'changePassword' | 'setPassword'): TranslationId =>
  buildTranslationId('setOrChangePassword.success', { context });
export const CHANGE_PASSWORD_FAILED_NOTIFICATION = buildTranslationId('setOrChangePassword.changePasswordFailed');
export const MSG_RESET_PASSWORD_SUCCESS = UPDATE_PASSWORD_SUCCESS_NOTIFICATION('changePassword');
export const MSG_RESET_PASSWORD_ALREADY_USED = UPDATE_PASSWORD_FAILED_ALREADY_USED_NOTIFICATION;
export const MSG_RESET_PASSWORD_USER_NOT_KNOWN = USER_NOT_KNOWN_NOTIFICATION;
export const MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL = UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL_NOTIFICATION('changePassword');
export const MSG_RESET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF =
  UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF_NOTIFICATION('changePassword');
export const MSG_RESET_PASSWORD_RESTORE_ID_EXPIRED = UPDATE_PASSWORD_RESTORE_ID_EXPIRED_NOTIFICATION('changePassword');
export const MSG_RESET_PASSWORD_DEFAULT = CHANGE_PASSWORD_FAILED_NOTIFICATION;
export const SET_PASSWORD_DEFAULT_NOTIFICATION = buildTranslationId('setOrChangePassword.setPasswordDefault');
export const MSG_SET_PASSWORD_SUCCESS = UPDATE_PASSWORD_SUCCESS_NOTIFICATION('setPassword');
export const MSG_SET_PASSWORD_ALREADY_USED = UPDATE_PASSWORD_FAILED_ALREADY_USED_NOTIFICATION;
export const MSG_SET_PASSWORD_USER_NOT_KNOWN = USER_NOT_KNOWN_NOTIFICATION;
export const MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL = UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_EMAIL_NOTIFICATION('setPassword');
export const MSG_SET_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF = UPDATE_PASSWORD_RESTORE_ID_NOT_MATCHING_VERIFICATION_REF_NOTIFICATION('setPassword');
export const MSG_SET_PASSWORD_RESTORE_ID_EXPIRED = UPDATE_PASSWORD_RESTORE_ID_EXPIRED_NOTIFICATION('setPassword');
export const MSG_SET_PASSWORD_DEFAULT = SET_PASSWORD_DEFAULT_NOTIFICATION;

// Authenticate email
export const MSG_AUTHENTICATE_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED = MAX_MAIL_REQUESTS_EXCEEDED_NOTIFICATION;
export const MSG_AUTHENTICATE_EMAIL_DEFAULT = buildTranslationId('authenticateEmail.default');

// Verify email authentication
export const MSG_VERIFY_EMAIL_AUTHENTICATION_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;

// Resend authentication email
export const MSG_RESEND_AUTHENTICATION_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED = MAX_MAIL_REQUESTS_EXCEEDED_NOTIFICATION;
export const MSG_RESEND_AUTHENTICATION_EMAIL_SERVER_UNREACHABLE = NEW_EMAIL_FAILED_SERVER_UNREACHABLE_NOTIFICATION(new ResendAuthenticationEmail());
export const MSG_RESEND_AUTHENTICATION_EMAIL_DEFAULT = NEW_EMAIL_FAILED_NOTIFICATION(new ResendAuthenticationEmail());
export const MSG_RESEND_AUTHENTICATION_EMAIL_SUCCESS = NEW_EMAIL_SENT_NOTIFICATION;

// Confirm authentication email
export const CONFIRM_SIGN_UP_SUCCESS = (context: 'academic' | 'checkout' | 'other'): TranslationId =>
  buildTranslationId('confirmAuthenticationEmail.signUpSuccess', { context });
export const MSG_CONFIRM_AUTHENTICATION_EMAIL_SIGN_UP_SUCCESS = CONFIRM_SIGN_UP_SUCCESS('other');
export const MSG_CONFIRM_AUTHENTICATION_EMAIL_CONFIRM_ID_NOT_MATCHING_EMAIL = buildTranslationId('confirmAuthenticationEmail.confirmIdNotMatchingEmail');
export const MSG_CONFIRM_AUTHENTICATION_EMAIL_CONFIRM_ID_EXPIRED = buildTranslationId('confirmAuthenticationEmail.confirmIdExpired');
export const MSG_CONFIRM_AUTHENTICATION_EMAIL_ALREADY_CONFIRMED = buildTranslationId('confirmAuthenticationEmail.alreadyConfirmed');

// Signed in with another account during sign up
export const MSG_EMAIL_AUTHENTICATION_SIGNED_IN_WITH_ANOTHER_ACCOUNT = SIGNED_IN_WITH_ANOTHER_ACCOUNT;

// Start OAuth
export const MSG_START_OAUTH_DEFAULT = (provider: string): TranslationId => buildTranslationId('startOauth.default', { provider });

// Verify OAuth
export const MSG_VERIFY_OAUTH_DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS = DUPLICATE_EMAIL_ADDRESS_DIFFERENT_ACCOUNTS;
export const MSG_VERIFY_OAUTH_NOT_FOUND = buildTranslationId('verifyOauth.notFound');
export const MSG_VERIFY_OAUTH_INVALID_STATE = OAUTH_AUTHENTICATION_FAILED;
export const MSG_VERIFY_OAUTH_SESSION_CORRUPTED = OAUTH_AUTHENTICATION_FAILED;
export const MSG_VERIFY_OAUTH_NO_EMAIL_AVAILABLE = (provider: string): TranslationId => buildTranslationId('verifyOauth.noEmailAvailable', { provider });
export const MSG_VERIFY_OAUTH_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;
export const MSG_VERIFY_OAUTH_LINK_ACCOUNT_CONFLICT = LINK_ACCOUNT_CONFLICT;
export const MSG_VERIFY_OAUTH_LINK_ACCOUNT_MISMATCH = LINK_ACCOUNT_MISMATCH;

// OAuth SignIn
export const MSG_OAUTH_SIGN_IN_FAILED = buildTranslationId('oauthSignIn.failed');

// OAuth SignUp
export const MSG_OAUTH_SIGN_UP_APPLE_EMAIL_HIDDEN = buildTranslationId('oauthSignUp.appleEmailHidden');

// Platform terms update
const PLATFORM_TERMS_OR_PRIVACY_UPDATE_NOTIFICATION = (path: YattaExternalLanguageSpecificLinkUrl, type: 'terms' | 'privacyPolicy'): TranslationId =>
  buildTranslationId('platformTermsOrPrivacyUpdate', {
    documentLink: buildYattaExternalLink(path, '_blank', 'link link-secondary link-underline', '166798c1-6f11-48c6-a660-50b2ef3d5e13'),
    type
  });
export const MSG_PLATFORM_TERMS_UPDATE = (termsPath: YattaExternalLanguageSpecificLinkUrl): TranslationId =>
  PLATFORM_TERMS_OR_PRIVACY_UPDATE_NOTIFICATION(termsPath, 'terms');
export const MSG_PLATFORM_PRIVACY_UPDATE = (privacyPath: YattaExternalLanguageSpecificLinkUrl): TranslationId =>
  PLATFORM_TERMS_OR_PRIVACY_UPDATE_NOTIFICATION(privacyPath, 'privacyPolicy');
export const MSG_PLATFORM_TERMS_AND_PRIVACY_UPDATE = (
  termsPath: YattaExternalLanguageSpecificLinkUrl,
  privacyPath: YattaExternalLanguageSpecificLinkUrl
): TranslationId =>
  buildTranslationId('platformTermsAndPrivacyUpdate', {
    termsLink: buildYattaExternalLink(termsPath, '_blank', 'link link-secondary link-underline', 'a231607e-bee3-4cd0-a58e-9f367e54f010'),
    privacyPolicyLink: buildYattaExternalLink(privacyPath, '_blank', 'link link-secondary link-underline', '49521bc3-6f8b-44fa-aab3-a86df0f5bc14')
  });
